import Spacer from '@/common/components/spacer';
import { Form } from 'react-router';
import ProceedToSchedule from '../proceed-to-schedule/proceed-to-schedule';
import MileagePlanSelection, { CreditConfig } from './mileage-plan-selection';
import PlanSelectionPanelContainer from './plan-selection-panel-container';
import usePackageSummary from '@/catalog/hooks/use-package-summary';
import { findPlanIdByTier as findInsurancePlanIdByTier } from '@/catalog/hooks/use-pdp-insurance-plan-submit';
import { findPlanIdByMileage as findMileagePlanIdByMileage } from '@/catalog/hooks/use-pdp-select-plan-submit';
import { VehiclePlan, InsurancePlanTier, InsurancePlan } from '@/catalog/types';
import { useMemo } from 'react';
import InsurancePlanSelection from './insurance-plan-selection';

interface SwapPlanSelectionPanelProps {
  // Mileage plans
  vehiclePlans: VehiclePlan[];
  currentMileagePlan?: number;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;

  // Insurance plans
  insurancePlans?: InsurancePlan[];
  currentInsurancePlanTier?: InsurancePlanTier;

  // Savings
  savingsAvailable?: boolean;
  estimatedSavings?: number;
  handleSavingsClick?: () => void;
  fullstoryOpenPlanPanelSavingsEventDelegate?: () => void;
  fullstoryOpenMobileStickyFooterSavingsEventDelegate?: () => void;

  // Additional config
  isOrderable: boolean;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  creditConfig: CreditConfig;
  fullstoryContinueEventDelegate?: () => void;
  isSubmittingContinue?: boolean;
  continueButtonRef?: (node?: Element | null) => void;
}

export default function SwapPlanSelectionPanel({
  // Mileage plans
  vehiclePlans,
  currentMileagePlan,
  mostPopularPlanMileageAmount,
  splitPayment = false,

  // Insurance plans
  insurancePlans,
  currentInsurancePlanTier,

  // Savings
  savingsAvailable,
  estimatedSavings,
  handleSavingsClick,
  fullstoryOpenPlanPanelSavingsEventDelegate,
  fullstoryOpenMobileStickyFooterSavingsEventDelegate,

  // Additional config
  isOrderable,
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  creditConfig,
  fullstoryContinueEventDelegate,
  continueButtonRef,
}: SwapPlanSelectionPanelProps) {
  const currentMileagePlanId = findMileagePlanIdByMileage(
    vehiclePlans,
    currentMileagePlan
  );
  const currentInsurancePlanId = findInsurancePlanIdByTier(
    insurancePlans,
    currentInsurancePlanTier
  );

  // Only show the current mileage plan for swap flow.
  const swapMileagePlans = useMemo(
    () =>
      vehiclePlans.filter(
        (mileagePlan) => mileagePlan.id === Number(currentMileagePlanId)
      ),
    [currentMileagePlanId, vehiclePlans]
  );

  // Only show current insurance plan for swap flow.
  const swapInsurancePlan = useMemo(
    () =>
      insurancePlans?.filter(
        (insurancePlans) => insurancePlans.id === Number(currentInsurancePlanId)
      ),
    [currentInsurancePlanId, insurancePlans]
  );

  const packageSummary = usePackageSummary({
    selectedMileagePlanId: currentMileagePlanId,
    selectedInsurancePlanId: currentInsurancePlanId,
    vehiclePlans: swapMileagePlans,
    insurancePlans: swapInsurancePlan,
    savingsAvailable,
    estimatedSavings,
  });

  // TODO: figure out what to do for weekly plans, maybe a banner or something (will wait for new UX before doing this)
  return (
    <PlanSelectionPanelContainer className="pb-[24px]" dataTestId="swap-panel">
      <Form method="post" className="flex flex-col px-[24px]">
        {/*
          Note: The selected mileage plan should be fixed to the current mileage plan for swaps. Users should not be able
            to change their mileage plan during the swap flow.
         */}
        <MileagePlanSelection
          // Mileage plans
          vehiclePlans={swapMileagePlans}
          selectedMileagePlanId={currentMileagePlanId}
          mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
          splitPayment={splitPayment}
          handleSelectMilagePlan={undefined}
          // Additional config
          // NOTE: The swap flow should not have any errors related to plan selection so we can hardcode the error config to default values
          errorConfig={{
            error: undefined,
            dismissedPlanGuide: true,
          }}
          inSwapFlow={true}
          isOrderable={isOrderable}
          price_savings_1200_over_850={price_savings_1200_over_850}
          price_per_mile_850_plan_for_1200_miles={price_per_mile_850_plan_for_1200_miles}
          price_difference_850_vs_1200={price_difference_850_vs_1200}
          creditConfig={creditConfig}
          hasNoSelectedAgeCreditRange={false}
        />

        <Spacer variant="height32" />

        {/*
          Note: The selected insurance plan should be fixed to the current insurance plan for swaps. Users should not be able
            to change their insurance plan during the swap flow.
         */}
        <InsurancePlanSelection
          // Insurance plans
          insurancePlans={insurancePlans}
          selectedInsurancePlanId={currentInsurancePlanId}
          handleSelectInsurancePlan={undefined}
          // Additional config
          // NOTE: The swap flow should not have any errors related to plan selection so we can hardcode the error config to default values
          errorConfig={{
            error: undefined,
            dismissedPlanGuide: true,
          }}
          hasSelectedMileagePlan={true}
          isOrderable={isOrderable}
          inSwapFlow={true}
          creditConfig={creditConfig}
          hasNoSelectedAgeCreditRange={false}
        />

        <Spacer variant="height40" hideForBreakpoints={['sm', 'md']} />

        <ProceedToSchedule
          isOrderable={isOrderable}
          packageSummary={packageSummary}
          fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
          fullstoryOpenPlanPanelSavingsEventDelegate={
            fullstoryOpenPlanPanelSavingsEventDelegate
          }
          fullstoryOpenMobileStickyFooterSavingsEventDelegate={
            fullstoryOpenMobileStickyFooterSavingsEventDelegate
          }
          continueButtonRef={continueButtonRef}
          handleSavingsClick={handleSavingsClick}
        />
      </Form>
    </PlanSelectionPanelContainer>
  );
}
